import type { Timeline } from "./timeline.js";

export interface SectionLayout {
  /** Frame the section is mounted at, relative to the whole composition. */
  from: number;
  durationInFrames: number;
}

export interface TransitionLayout {
  /** Frame at which the outgoing and incoming sections start overlapping. */
  from: number;
  durationInFrames: number;
}

export interface TimelineLayout {
  fps: number;
  width: number;
  height: number;
  totalFrames: number;
  head?: SectionLayout;
  body: SectionLayout;
  end?: SectionLayout;
  headToBody?: TransitionLayout;
  bodyToEnd?: TransitionLayout;
}

const toFrames = (seconds: number, fps: number) => Math.max(0, Math.round(seconds * fps));

/**
 * The one place section timing is computed. The editor preview, the renderer and
 * the duration shown in the UI all call this, which is what keeps the preview
 * and the exported file frame-identical.
 *
 * Transitions overlap their neighbours: a 0.6s HEAD->BODY transition pulls BODY
 * 0.6s earlier rather than inserting a gap.
 *
 * `atFps` exists because a timeline can legitimately be rendered at a rate
 * other than its own — the render page offers 30fps for a 60fps project. The
 * timeline stores seconds, so the same document lays out correctly at any rate,
 * but the frame numbers must be computed at the rate the composition is
 * actually mounted with. Using the timeline's own rate while Remotion counts
 * frames at another puts every section at the wrong frame: at half the rate the
 * END card is scheduled past the last frame and never appears at all.
 */
export function computeLayout(timeline: Timeline, atFps?: number): TimelineLayout {
  const fps = atFps && atFps > 0 ? atFps : timeline.fps;
  const head = timeline.sections.head?.enabled ? timeline.sections.head : undefined;
  const end = timeline.sections.end?.enabled ? timeline.sections.end : undefined;

  const headFrames = head ? toFrames(head.duration, fps) : 0;
  const bodyFrames = toFrames(timeline.sections.body.duration, fps);
  const endFrames = end ? toFrames(end.duration, fps) : 0;

  const headOverlap = head ? toFrames(timeline.transitions.headToBody?.duration ?? 0, fps) : 0;
  const endOverlap = end ? toFrames(timeline.transitions.bodyToEnd?.duration ?? 0, fps) : 0;

  const bodyFrom = Math.max(0, headFrames - headOverlap);
  const endFrom = Math.max(0, bodyFrom + bodyFrames - endOverlap);

  const layout: TimelineLayout = {
    fps,
    width: timeline.width,
    height: timeline.height,
    totalFrames: Math.max(1, endFrom + endFrames || bodyFrom + bodyFrames),
    body: { from: bodyFrom, durationInFrames: bodyFrames },
  };

  if (head) layout.head = { from: 0, durationInFrames: headFrames };
  if (end) layout.end = { from: endFrom, durationInFrames: endFrames };
  if (head && headOverlap > 0) layout.headToBody = { from: bodyFrom, durationInFrames: headOverlap };
  if (end && endOverlap > 0) layout.bodyToEnd = { from: endFrom, durationInFrames: endOverlap };

  return layout;
}

/** Total runtime of the finished video in seconds. */
export function timelineDuration(timeline: Timeline): number {
  const layout = computeLayout(timeline);
  return layout.totalFrames / layout.fps;
}

/** `mm:ss` for UI display. */
export function formatTimecode(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const mins = Math.floor(safe / 60);
  const secs = Math.floor(safe % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}
